// SuggestionRail — 320px right-rail surfacing AI suggestions for the active
// query. Three collapse states: 24px gutter / 320 pinned / 480 expanded.
//
// Cards categorized: HIGH-FIT, INCUMBENT-AT-RISK, ANOMALY, SHAPING_WINDOW.
// Per-card dismiss; per-category mute hint shown after 3 dismissals on the
// same query shape (the host owns the dismissal store).
//
// THIS RAIL DOES NOT FETCH. The host fetches /v1/ai/suggest and passes
// `suggestions` (typed via @eidollon/contracts/search). Keeps this module
// pure and testable.

import type { search } from '@eidollon/contracts';
import type { CSSProperties, ReactNode } from 'react';
import { useIsMobile } from './useBreakpoint.js';

export type RailMode = 'collapsed' | 'pinned' | 'expanded';

export interface SuggestionRailProps {
  mode: RailMode;
  onModeChange: (next: RailMode) => void;
  suggestions: search.Suggestion[];
  onOpen: (s: search.Suggestion) => void;
  onDismiss: (s: search.Suggestion) => void;
  onMuteCategory: (kind: search.SuggestionKind) => void;
  loading?: boolean;
  /** When `meta.coldStart` is true, host should set `coldStart` so we
   *  display the right empty-state. */
  coldStart?: boolean;
}

const KIND_LABEL: Record<search.SuggestionKind, string> = {
  high_fit: 'HIGH-FIT',
  incumbent_at_risk: 'INCUMBENT-AT-RISK',
  anomaly: 'ANOMALY',
  shaping_window: 'SHAPING WINDOW',
};

const KIND_TONE: Record<search.SuggestionKind, string> = {
  high_fit: 'var(--primary)',
  incumbent_at_risk: 'var(--amber)',
  anomaly: 'var(--magenta, #d94cb6)',
  shaping_window: 'var(--green)',
};

export function SuggestionRail({
  mode,
  onModeChange,
  suggestions,
  onOpen,
  onDismiss,
  onMuteCategory,
  loading,
  coldStart,
}: SuggestionRailProps) {
  const isMobile = useIsMobile();

  // ── Mobile: a bottom sheet, not a side rail ──────────────────────────────
  // A fixed 320/480px right rail can't fit a phone. Collapsed → a small pill
  // floating above the bottom tab bar; open → a bottom sheet (60dvh pinned,
  // 92dvh expanded) over a tap-to-dismiss backdrop. Same card body as desktop.
  if (isMobile) {
    if (mode === 'collapsed') {
      return (
        <button
          type="button"
          onClick={() => onModeChange('pinned')}
          aria-label="open AI suggestions"
          style={mobilePillStyle}
        >
          AI · {suggestions.length}
        </button>
      );
    }
    return (
      <>
        <div
          aria-hidden="true"
          onClick={() => onModeChange('collapsed')}
          style={sheetBackdropStyle}
        />
        <aside
          data-component="SuggestionRail"
          data-mode={mode}
          data-mobile="true"
          style={{
            ...sheetStyle,
            height: mode === 'expanded' ? '92dvh' : '60dvh',
          }}
        >
          <header style={sheetHeaderStyle}>
            <span
              style={{
                color: 'var(--fg-3)',
                fontFamily: 'var(--mono)',
                fontSize: 11,
                letterSpacing: 0.4,
              }}
            >
              AI ASSIST · {suggestions.length}
            </span>
            <span style={{ display: 'flex', gap: 8 }}>
              <RailToggle
                title="expand"
                active={mode === 'expanded'}
                onClick={() => onModeChange(mode === 'expanded' ? 'pinned' : 'expanded')}
              >
                ⤢
              </RailToggle>
              <RailToggle title="close" onClick={() => onModeChange('collapsed')}>
                ×
              </RailToggle>
            </span>
          </header>
          <RailBody
            suggestions={suggestions}
            loading={loading}
            coldStart={coldStart}
            onOpen={onOpen}
            onDismiss={onDismiss}
            onMuteCategory={onMuteCategory}
            expanded={mode === 'expanded'}
          />
        </aside>
      </>
    );
  }

  if (mode === 'collapsed') {
    return (
      <button
        type="button"
        onClick={() => onModeChange('pinned')}
        title="open AI suggestions"
        aria-label="open AI suggestions"
        style={{
          width: 24,
          flexShrink: 0,
          background: 'var(--bg-pane)',
          borderLeft: '1px solid var(--bd)',
          color: 'var(--fg-3)',
          fontFamily: 'var(--mono)',
          fontSize: 10,
          letterSpacing: 0.4,
          cursor: 'pointer',
          writingMode: 'vertical-rl',
          padding: '8px 0',
        }}
      >
        AI · {suggestions.length}
      </button>
    );
  }

  const width = mode === 'pinned' ? 320 : 480;

  return (
    <aside
      data-component="SuggestionRail"
      data-mode={mode}
      style={{
        width,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--bg-pane)',
        borderLeft: '1px solid var(--bd)',
        minHeight: 0,
      }}
    >
      <header
        style={{
          height: 28,
          padding: '0 10px',
          borderBottom: '1px solid var(--bd)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}
      >
        <span
          style={{
            color: 'var(--fg-3)',
            fontFamily: 'var(--mono)',
            fontSize: 10,
            letterSpacing: 0.4,
          }}
        >
          AI ASSIST · {suggestions.length}
        </span>
        <span style={{ display: 'flex', gap: 6 }}>
          <RailToggle
            title="expand"
            active={mode === 'expanded'}
            onClick={() => onModeChange(mode === 'expanded' ? 'pinned' : 'expanded')}
          >
            ⤢
          </RailToggle>
          <RailToggle title="collapse" onClick={() => onModeChange('collapsed')}>
            −
          </RailToggle>
        </span>
      </header>

      <RailBody
        suggestions={suggestions}
        loading={loading}
        coldStart={coldStart}
        onOpen={onOpen}
        onDismiss={onDismiss}
        onMuteCategory={onMuteCategory}
        expanded={mode === 'expanded'}
      />
    </aside>
  );
}

// Shared scroll body — the grouped suggestion cards + empty states. Rendered
// by both the desktop side rail and the mobile bottom sheet so they never
// drift.
function RailBody({
  suggestions,
  loading,
  coldStart,
  onOpen,
  onDismiss,
  onMuteCategory,
  expanded,
}: {
  suggestions: search.Suggestion[];
  loading?: boolean;
  coldStart?: boolean;
  onOpen: (s: search.Suggestion) => void;
  onDismiss: (s: search.Suggestion) => void;
  onMuteCategory: (kind: search.SuggestionKind) => void;
  expanded: boolean;
}) {
  const grouped = groupByKind(suggestions);
  return (
    <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: 8 }}>
      {loading && suggestions.length === 0 && <div style={emptyStyle}>loading suggestions…</div>}
      {!loading && suggestions.length === 0 && coldStart && (
        <div style={emptyStyle}>
          COLD START · the more you use the terminal, the better these get. Save a screen, open a
          brief, draft a bid.
        </div>
      )}
      {!loading && suggestions.length === 0 && !coldStart && (
        <div style={emptyStyle}>no suggestions for the current query.</div>
      )}
      {Object.entries(grouped).map(([kind, items]) => (
        <section key={kind} style={{ marginBottom: 12 }}>
          <header
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '4px 4px',
              color: KIND_TONE[kind as search.SuggestionKind],
              fontFamily: 'var(--mono)',
              fontSize: 10,
              letterSpacing: 0.4,
            }}
          >
            <span>
              ▣ {KIND_LABEL[kind as search.SuggestionKind]} · {items.length}
            </span>
            <button
              type="button"
              onClick={() => onMuteCategory(kind as search.SuggestionKind)}
              title="mute this category for 24h"
              style={{
                background: 'transparent',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                color: 'var(--fg-3)',
                fontFamily: 'var(--mono)',
                fontSize: 10,
              }}
            >
              MUTE
            </button>
          </header>
          {items.map((s) => (
            <SuggestionCard
              key={s.id}
              suggestion={s}
              onOpen={() => onOpen(s)}
              onDismiss={() => onDismiss(s)}
              expanded={expanded}
            />
          ))}
        </section>
      ))}
    </div>
  );
}

function SuggestionCard({
  suggestion,
  onOpen,
  onDismiss,
  expanded,
}: {
  suggestion: search.Suggestion;
  onOpen: () => void;
  onDismiss: () => void;
  expanded: boolean;
}) {
  return (
    <article
      style={{
        padding: 6,
        borderTop: '1px solid var(--bd-soft, var(--bd))',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
        <ConfidenceDot value={suggestion.confidence} />
        <span
          style={{
            flex: 1,
            color: 'var(--fg-1)',
            fontFamily: 'var(--mono)',
            fontSize: 11,
            lineHeight: '16px',
          }}
        >
          {suggestion.label}
        </span>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="dismiss"
          title="dismiss"
          style={{
            background: 'transparent',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            color: 'var(--fg-3)',
            fontSize: 12,
            lineHeight: '12px',
          }}
        >
          ×
        </button>
      </div>
      {expanded && suggestion.why.length > 0 && (
        <ul style={{ listStyle: 'none', margin: 0, padding: '0 0 0 18px' }}>
          {suggestion.why.map((line, i) => (
            <li
              key={i}
              style={{
                color: 'var(--fg-2)',
                fontFamily: 'var(--mono)',
                fontSize: 11,
                lineHeight: '16px',
              }}
            >
              · {line}
            </li>
          ))}
        </ul>
      )}
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 6,
          marginTop: 2,
        }}
      >
        <button
          type="button"
          onClick={onOpen}
          style={{
            background: 'var(--bg-pane-2)',
            border: '1px solid var(--bd-strong)',
            color: 'var(--fg-1)',
            padding: '0 6px',
            height: 20,
            cursor: 'pointer',
            fontFamily: 'var(--mono)',
            fontSize: 10,
            letterSpacing: 0.4,
          }}
        >
          OPEN
        </button>
      </div>
    </article>
  );
}

function ConfidenceDot({ value }: { value: number }) {
  const pct = Math.round(value * 100);
  const fill = value >= 0.6 ? 'var(--primary)' : 'var(--fg-3)';
  return (
    <span
      title={`${pct}% confidence`}
      style={{
        width: 10,
        height: 10,
        borderRadius: '50%',
        background: fill,
        flexShrink: 0,
        marginTop: 4,
      }}
    />
  );
}

function RailToggle({
  title,
  onClick,
  children,
  active,
}: {
  title: string;
  onClick: () => void;
  children: ReactNode;
  active?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      style={{
        background: active ? 'var(--primary-soft, rgba(52,120,246,0.18))' : 'transparent',
        border: '1px solid var(--bd-strong)',
        color: 'var(--fg-2)',
        padding: 0,
        width: 18,
        height: 18,
        cursor: 'pointer',
        fontFamily: 'var(--mono)',
        fontSize: 10,
        lineHeight: '18px',
      }}
    >
      {children}
    </button>
  );
}

function groupByKind(
  suggestions: search.Suggestion[],
): Record<search.SuggestionKind, search.Suggestion[]> {
  const out: Record<string, search.Suggestion[]> = {};
  for (const s of suggestions) {
    (out[s.kind] ??= []).push(s);
  }
  return out as Record<search.SuggestionKind, search.Suggestion[]>;
}

const emptyStyle: CSSProperties = {
  padding: 12,
  textAlign: 'center',
  color: 'var(--fg-3)',
  fontFamily: 'var(--mono)',
  fontSize: 11,
  lineHeight: '16px',
};

// ── Mobile bottom-sheet styles ────────────────────────────────────────────
const mobilePillStyle: CSSProperties = {
  position: 'fixed',
  right: 12,
  // sit above the 52px bottom tab bar + the iOS home indicator
  bottom: 'calc(52px + env(safe-area-inset-bottom, 0px) + 12px)',
  zIndex: 25,
  height: 36,
  padding: '0 14px',
  borderRadius: 18,
  background: 'var(--bg-pane-2, var(--bg-pane))',
  border: '1px solid var(--bd-strong, var(--bd))',
  color: 'var(--fg-2)',
  fontFamily: 'var(--mono)',
  fontSize: 11,
  letterSpacing: 0.4,
  cursor: 'pointer',
  boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
};

const sheetBackdropStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.5)',
  zIndex: 40,
};

const sheetStyle: CSSProperties = {
  position: 'fixed',
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 41,
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  background: 'var(--bg-pane)',
  borderTop: '1px solid var(--bd-strong, var(--bd))',
  borderTopLeftRadius: 12,
  borderTopRightRadius: 12,
  paddingBottom: 'env(safe-area-inset-bottom, 0px)',
  boxShadow: '0 -4px 24px rgba(0,0,0,0.4)',
};

const sheetHeaderStyle: CSSProperties = {
  height: 44,
  padding: '0 14px',
  borderBottom: '1px solid var(--bd)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexShrink: 0,
};
